import {COLLECTION_CAPS} from './render-guards.js';
import {normalizeCollectionState} from './state.js';
import {collectionRequestKeys, createCollectionCache, pollCollection} from './requests.js';

const abortError = () => Object.assign(new Error('Superseded collection request'), {name: 'AbortError'});
const freeze = value => {
  if (value && typeof value === 'object') { Object.values(value).forEach(freeze); Object.freeze(value); }
  return value;
};
const pending = data => ['processing','queued','running','pending'].includes(data?.state);
const cameraFields = camera => Object.fromEntries(['position','target','viewUp','viewAngle','parallelScale'].filter(key => camera?.[key] !== undefined).map(key => [key, camera[key]]));

/** Renderer-independent contract. One instance belongs to one authorized browser identity. */
export function createCollectionController({client, initialState, identity, document = globalThis.document, onChange = () => {}, announce = () => {}, onFocus = () => {}, pollOptions = {}, cacheBytes} = {}) {
  let scientific = freeze(normalizeCollectionState(initialState));
  let principal = identity;
  let transient = {table: {page: 1, page_size: 25}, genePage: {offset: 0, limit: 32}, viewport: {}, summary: {groupings: ['class','region']}};
  let results = {};
  let query = null;
  let queryFlight = null;
  let queryController = null;
  let generation = 0;
  let disposed = false;
  let denied = false;
  const channels = new Map();
  const jobs = new Map();
  const cache = createCollectionCache(cacheBytes);
  const snapshot = () => ({scientific, transient: structuredClone(transient), query: query ? {...query} : null, results: {...results}, denied});
  const emit = () => onChange(snapshot());
  const cancelJob = id => {
    // Best effort, never bypass current server authorization. No retry loop.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 5000);
    Promise.resolve().then(() => client.cancelCollectionJob(id, controller.signal)).catch(() => {}).finally(() => clearTimeout(timer));
  };
  const cancelJobs = lane => {
    for (const [id, owners] of jobs) {
      if (lane) owners.delete(lane); else owners.clear();
      if (!owners.size) { jobs.delete(id); cancelJob(id); }
    }
  };
  const cancel = lane => { channels.get(lane)?.abort(); channels.delete(lane); cancelJobs(lane); delete results[lane]; };
  const clear = () => {
    generation++;
    for (const lane of channels.keys()) cancel(lane);
    queryController?.abort(); queryController = null; queryFlight = null; query = null;
    cancelJobs(); cache.clear(); results = {};
  };
  // Collection work is durable server work. A researcher switching tabs must
  // not discard a long-running membership or spatial build; the workspace
  // refreshes its reads when the tab becomes visible again.
  const available = () => !disposed && !denied;
  const track = (data, lane, signal, epoch) => {
    const id = data?.spatial_id || data?.summary_id || data?.order_id || data?.export_id || data?.query_id;
    if (id && pending(data)) {
      if (signal.aborted || epoch !== generation || !available()) { if (!jobs.has(id)) cancelJob(id); }
      else {
        const owners = jobs.get(id) || new Set();
        owners.add(lane); jobs.set(id, owners);
      }
    }
    signal.throwIfAborted();
    if (epoch !== generation || !available()) throw abortError();
    return data;
  };
  const finish = data => {
    for (const key of ['query_id','spatial_id','summary_id','order_id','export_id']) if (data?.[key]) jobs.delete(data[key]);
    return data;
  };
  const ready = async (initial, lane, signal, read, epoch) => {
    track(initial, lane, signal, epoch);
    // Whole-collection jobs can queue behind other work. Keep watching for
    // up to ~15 minutes, with one request every five seconds after backoff.
    const options = {maxAttempts:180, ...pollOptions};
    const data = await pollCollection(async () => pending(initial) ? read(signal) : initial, signal, options);
    finish(initial);
    return finish(data);
  };
  const ensureQuery = () => {
    if (query) return Promise.resolve(query);
    if (queryFlight) return queryFlight;
    const epoch = generation;
    const state = scientific;
    const controller = new AbortController();
    queryController = controller;
    const flight = (async () => {
      const initial = await client.createCollectionQuery({dataset_id: state.datasetId, version: state.version, content_sha256: state.collection.content_sha256, ...state.filters}, controller.signal);
      const data = await ready(initial, 'membership', controller.signal, signal => client.getCollectionQuery(initial.query_id, signal), epoch);
      controller.signal.throwIfAborted();
      if (epoch !== generation || !available()) throw abortError();
      if (!data.query_id || data.state !== 'ready') throw Object.assign(new Error('Invalid query handle'), {code: 'invalid_response'});
      query = freeze(data); emit(); return query;
    })();
    queryFlight = flight;
    // Don't let an old finally remove a newer flight.
    flight.finally(() => { if (queryFlight === flight) queryFlight = null; }).catch(() => {});
    return flight;
  };
  const renew = lane => {
    queryController?.abort(); query = null; queryFlight = null;
    cancelJobs('membership');
    for (const other of channels.keys()) if (other !== lane) cancel(other);
    cancelJobs(lane); results = {}; cache.clear();
    transient.table = {...transient.table, page: 1}; delete transient.table.cursor;
    emit();
  };
  const run = async (lane, operation, usesQuery = true) => {
    cancel(lane);
    if (!available()) return undefined;
    const controller = new AbortController();
    const epoch = generation;
    channels.set(lane, controller);
    const current = () => channels.get(lane) === controller && !controller.signal.aborted && epoch === generation && available();
    results[lane] = {state: 'processing', data: null}; emit();
    try {
      let data;
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const handle = usesQuery ? await ensureQuery() : null;
          if (!current()) return undefined;
          data = await operation(handle?.query_id, controller.signal, epoch);
          break;
        } catch (error) {
          if (!current()) return undefined;
          if (error.code !== 'expired_query' || attempt || !usesQuery) throw error;
          renew(lane);
        }
      }
      if (!current()) return undefined;
      // Spatial payloads are owned by this lane and validated by the scene.
      // Do not retain an unused serialized copy or recursively freeze 250k cells.
      if (lane !== 'tiles') cache.put(principal, collectionRequestKeys(scientific, transient)[lane] || lane, data);
      results[lane] = {state: 'ready', data: lane === 'tiles' ? Object.freeze(data) : freeze(data)}; emit();
      return data;
    } catch (error) {
      if (!current() || error.name === 'AbortError') return undefined;
      if ([401,403].includes(error.status) || (error.status === 404 && lane !== 'evidence')) { clear(); denied = true; }
      else if (error.code === 'poll_limit') {
        // A browser polling budget is not a server cancellation. Detach all
        // ownership so the final waiter and a later Retry retain queued work.
        jobs.clear();
        results[lane] = {state: 'waiting', data: null, error};
      } else { cancelJobs(lane); results[lane] = {state: 'error', data: null, error}; }
      emit(); throw error;
    } finally {
      if (channels.get(lane) === controller) channels.delete(lane);
      if (!channels.size && !query) { queryController?.abort(); cancelJobs('membership'); }
    }
  };
  const update = (patch) => {
    const before = collectionRequestKeys(scientific, transient);
    const previous = scientific;
    scientific = freeze(normalizeCollectionState({...scientific, ...patch}));
    const after = collectionRequestKeys(scientific, transient);
    if (JSON.stringify(previous.tableOrder) !== JSON.stringify(scientific.tableOrder)) { transient.table = {...transient.table, page: 1}; delete transient.table.cursor; }
    if (before.membership !== after.membership) {
      clear(); transient.table = {...transient.table, page: 1}; delete transient.table.cursor;
      if (previous.datasetId !== scientific.datasetId || previous.version !== scientific.version || JSON.stringify(previous.collection) !== JSON.stringify(scientific.collection)) denied = false;
    } else for (const lane of Object.keys(after)) if (before[lane] !== after[lane]) cancel(lane);
    if (JSON.stringify(previous.frame) !== JSON.stringify(scientific.frame)) { transient.viewport = {}; cancel('tiles'); }
    emit(); return snapshot();
  };
  const setTransient = (key, value) => {
    const before = collectionRequestKeys(scientific, transient);
    transient = {...transient, [key]: structuredClone(value)};
    const after = collectionRequestKeys(scientific, transient);
    for (const lane of Object.keys(after)) if (before[lane] !== after[lane]) cancel(lane);
    emit();
  };
  return {
    getState: snapshot,
    get cacheBytes() { return cache.bytes; },
    update,
    applyFilters(filters,statePatch={}) {
      const before = collectionRequestKeys(scientific, transient).membership;
      const stalled = Object.values(results).some(result => ['waiting','error'].includes(result.state));
      update({...statePatch,filters});
      return stalled && before === collectionRequestKeys(scientific, transient).membership;
    },
    select(entityId) { update({selectedEntityId: entityId}); },
    focus() { if (scientific.selectedEntityId) onFocus(scientific.selectedEntityId, scientific.frame); },
    setIdentity(value) { if (value !== principal) { clear(); principal = value; denied = value == null; emit(); } },
    revoke() { clear(); denied = true; emit(); },
    dispose() { disposed = true; clear(); },
    cancel,
    loadTable(page = transient.table) {
      if (page.gene_ids && (!Array.isArray(page.gene_ids) || page.gene_ids.length > 32)) throw new Error('Table gene page exceeds 32 genes');
      const bounded = {...Object.fromEntries(['page','cursor','fields','gene_ids'].filter(key => page[key] !== undefined).map(key => [key, page[key]])), page_size: Math.max(1, Math.min(100, Math.floor(page.page_size || 25)))};
      setTransient('table', bounded);
      return run('table', async (id, signal, epoch) => {
        const order = scientific.tableOrder;
        if (order.sort !== 'entity_id') track(await client.createCollectionOrder(id, order, signal), 'table', signal, epoch);
        const result = await pollCollection(s => client.getCollectionTable(id, {...transient.table, ...order}, s), signal, pollOptions);
        // Order response IDs are not repeated by every table payload.
        for (const [job, owners] of jobs) if (owners.has('table')) jobs.delete(job);
        return result;
      });
    },
    loadAdjacent() {
      const selected = scientific.selectedEntityId;
      return run('adjacent', async (id, signal, epoch) => {
        const order = scientific.tableOrder;
        if (order.sort !== 'entity_id') track(await client.createCollectionOrder(id, order, signal), 'adjacent', signal, epoch);
        const result = await pollCollection(s => client.getCollectionAdjacent(id, {...order, selected, page_size: transient.table.page_size}, s), signal, pollOptions);
        for (const [job, owners] of jobs) if (owners.has('adjacent')) jobs.delete(job);
        return result;
      }).then(data => {
        if (data?.selection_state === 'no_match' && scientific.selectedEntityId === selected) {
          update({selectedEntityId: null});
          announce('The selected cell is outside the current filters. Selection cleared.');
        }
        return data;
      });
    },
    loadEncoding() {
      return run('encoding', (_, signal) => client.request('/genes/', {dataset_id: scientific.datasetId, version: scientific.version, gene_id: scientific.colorGeneId, page_size: 1}, signal, {cache: 'no-store'}), false);
    },
    loadEvidence(genePage = transient.genePage) {
      if (genePage.gene_ids && (!Array.isArray(genePage.gene_ids) || genePage.gene_ids.length > 32)) throw new Error('Evidence gene page exceeds 32 genes');
      const bounded = {offset: Math.max(0, Math.floor(genePage.offset || 0)), ...(genePage.gene_ids ? {gene_ids: genePage.gene_ids} : {}), limit: Math.max(1, Math.min(32, Math.floor(genePage.limit || 32)))};
      setTransient('genePage', bounded);
      return run('evidence', (_, signal) => client.getCollectionEvidence(scientific.selectedEntityId, {dataset_id: scientific.datasetId, version: scientific.version, ...bounded}, signal), false);
    },
    loadTiles(viewport) {
      if (!Array.isArray(viewport.tiles) || viewport.tiles.length > 64 || viewport.tiles.some(tile => typeof tile !== 'string' || !/^[0-7]{0,12}$/.test(tile))) throw new Error('Invalid bounded tiles');
      const spec = {...(scientific.colorGeneId ? {color_gene_id:scientific.colorGeneId} : {}), frame: scientific.frame.frame_id, tiles: [...new Set(viewport.tiles)].sort(), depth: Math.max(0, Math.min(12, Math.floor(viewport.depth ?? 6))), limit: Math.max(1, Math.min(COLLECTION_CAPS.points, Math.floor(viewport.limit || 10000))), camera: cameraFields(viewport.camera || scientific.camera), ...(viewport.frustum ? {frustum: viewport.frustum} : {}), ...(scientific.selectedEntityId ? {selected: scientific.selectedEntityId} : {})};
      setTransient('viewport', spec);
      return run('tiles', async (id, signal, epoch) => {
        const data = await client.createCollectionTiles({query_id: id, ...spec}, signal);
        const result = await ready(data, 'tiles', signal, s => client.getCollectionTiles(data.spatial_id, s, spec.color_gene_id), epoch);
        if (!Array.isArray(result.points) || result.points.length > spec.limit) throw Object.assign(new Error('Invalid bounded points'), {code: 'invalid_response'});
        return result;
      });
    },
    loadSummary(spec = transient.summary) {
      const normalized = {inclusive_parents: spec.inclusive_parents === true, groupings: [...new Set(spec.groupings || ['region','class'])].sort(), gene_ids: [...new Set(spec.gene_ids || [])].sort()};
      setTransient('summary', normalized);
      return run('summary', async (id, signal, epoch) => {
        const data = await client.createCollectionSummary({query_id: id, ...normalized}, signal);
        return ready(data, 'summary', signal, s => client.getCollectionSummary(data.summary_id, s), epoch);
      });
    },
    estimateExport(spec) {
      setTransient('export', spec);
      return run('export', (id, signal) => client.estimateCollectionExport({...spec, query_id: id}, signal));
    },
    createExport(spec, confirmedHash) {
      setTransient('export', {...spec, confirmed_hash: confirmedHash});
      return run('export', async (id, signal, epoch) => {
        const data = await client.createCollectionExport({...spec, query_id: id, confirmed_hash: confirmedHash}, signal);
        return ready(data, 'export', signal, s => client.getCollectionExport(data.export_id, s), epoch);
      });
    },
  };
}
